import React, { useState } from 'react';
import { useGetAllCommentsQuery, useDeleteCommentMutation, IComment } from '../../redux/api/commentApi';
import { Table, Button, Popconfirm, message, Avatar, Space, Typography } from 'antd';
import { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

const CommentsPage: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const { data, isLoading, isFetching, refetch } = useGetAllCommentsQuery({ page, limit: pageSize });
    const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

    const handleDelete = async (commentId: string) => {
        try {
            await deleteComment(commentId).unwrap();
            message.success('Comment deleted successfully');
            refetch(); // Optimistic update might be better but refetch is safer
        } catch (err: any) {
            message.error(err?.data?.message || 'Failed to delete comment');
        }
    };

    const columns: ColumnsType<IComment> = [
        {
            title: 'User',
            key: 'user',
            render: (_, record) => (
                <Space>
                    <Avatar src={record.userProfilePicture} icon={<UserOutlined />} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text strong>{record.userName}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>{record.userEmail}</Text>
                    </div>
                </Space>
            ),
        },
        {
            title: 'Content',
            key: 'content',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text strong>{record.contentType}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>ID: {record.contentId.substring(0, 8)}...</Text>
                </div>
            ),
        },
        {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
            width: '40%',
            render: (text, record) => (
                <div>
                    <Text>{text}</Text>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                        ❤️ {record.likes}
                    </div>
                </div>
            )
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => new Date(date).toLocaleDateString(),
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure you want to delete this comment?"
                    onConfirm={() => handleDelete(record.commentId)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button danger icon={<DeleteOutlined />} loading={isDeleting} size="small">
                        Delete
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Manage Comments</Title>

            <Table
                columns={columns}
                dataSource={data?.comments}
                rowKey="commentId"
                loading={isLoading || isFetching}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: data?.total || 0,
                    onChange: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                    },
                    showSizeChanger: true
                }}
                bordered
            />
        </div>
    );
};

export default CommentsPage;
